import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, Outlet } from "react-router-dom";
import useAutoLogout from "../../util_functions/UseAutoLogout";
import {
  FaBriefcase,
  FaDollarSign,
  FaUserCog,
  FaRegListAlt,
  FaRegBell,
  FaEdit,
  FaSignOutAlt,
} from "react-icons/fa";
import { useMediaQuery } from "react-responsive";
import { TiMessages } from "react-icons/ti";
import { MdDashboard } from "react-icons/md";
import { ImUsers } from "react-icons/im";
import AdminAccountDashboardNavbar from "../../components/administration/AdminAccountDashboardNavbar";
import config from "../../config/config";
import axios from "axios";
import { useAdminUser } from "../../context/AdminUserContext";
import { useUser } from "../../context/UserContext";
import WarningModal from "../../util_functions/WarningModal";
import { ThreeDots } from "react-loader-spinner";
import useOutsideClick from "../../util_functions/UseOutsideClick";

const Dashboard = () => {
  const {
    loggedInAdminUser,
    setLoggedInAdminUser,
    fetchLoggedInAdminUser,
    adminUser,
    logout,
  } = useAdminUser();
  const { totalUnverifiedWorkerCount, getUnverifiedWorkerUsersAndCounts } =
    useUser();
  const [showMenu, setShowMenu] = useState(false);

  const navigate = useNavigate();

  const { resetTimer, showWarning, remainingTime } = useAutoLogout(logout);

  const baseURL = config.API_BASE_URL;

  const [showSidebar, setShowSidebar] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const dashboardMainContainerRef = useRef(null);
  const dashboardContainerRef = useRef(null);
  const sidebarRef = useRef(null);
  const contentRef = useRef(null);
  const navbarRef = useRef(null);
  const notificationIconRef = useRef(null);
  const accountIconRef = useRef(null);

  const [showNotificationPane, setShowNotificationPane] = useState({
    show: false,
    top: 0,
    left: 0,
    bottom: null,
    iconWidth: 0,
  });

  const [showAccountPane, setShowAccountPane] = useState({
    show: false,
    top: 0,
    left: 0,
    right: 0,
  });

  const notificationPaneRef = useOutsideClick(() => {
    setShowNotificationPane((prev) => ({ ...prev, show: false }));
  }, [notificationIconRef]);

  const accountPaneRef = useOutsideClick(() => {
    setShowAccountPane((prev) => ({ ...prev, show: false }));
  }, [accountIconRef]);

  useEffect(() => {
    const adjustLayout = () => {
      if (
        navbarRef.current &&
        dashboardContainerRef.current &&
        dashboardMainContainerRef.current &&
        sidebarRef.current
      ) {
        const navbarHeight = navbarRef.current.offsetHeight;
        setNavbarHeight(navbarHeight);

        sidebarRef.current.style.top = `${navbarHeight}px`;
        sidebarRef.current.style.height = `calc(100% - ${navbarHeight}px)`;

        const dashboardContainerHeight =
          dashboardContainerRef.current.offsetHeight;
        const dashboardMainContainerHeight =
          dashboardMainContainerRef.current.offsetHeight;

        if (isMobile) {
          const marginTopValue = -(
            dashboardMainContainerHeight -
            (dashboardContainerHeight + navbarHeight)
          );
          console.log("Margin Top Value:", marginTopValue);
          dashboardContainerRef.current.style.marginTop = `${marginTopValue}px`;
        } else {
          dashboardContainerRef.current.style.marginTop = "0px";
          dashboardContainerRef.current.style.marginBottom = "0px";
        }

        console.log("Is Mobile:", isMobile);
        console.log("Navbar height:", navbarHeight);
        console.log("Dashboard container height:", dashboardContainerHeight);
        console.log(
          "Dashboard main container height:",
          dashboardMainContainerHeight
        );
      }
    };

    adjustLayout();
    window.addEventListener("resize", adjustLayout);
    return () => window.removeEventListener("resize", adjustLayout);
  }, [isMobile, navbarHeight]);

  useEffect(() => {
    if (!loggedInAdminUser) {
      fetchLoggedInAdminUser()
        .then((userArray) => {
          if (userArray && userArray.length > 0) {
            const user = userArray[0];
            setLoggedInAdminUser(user);
          } else {
            console.log("No logged-in user found.");
          }
        })
        .catch((error) => {
          console.error("Error fetching logged-in user:", error);
        });
    }
  }, [loggedInAdminUser, fetchLoggedInAdminUser, setLoggedInAdminUser]);

  useEffect(() => {
    getUnverifiedWorkerUsersAndCounts();
  }, [getUnverifiedWorkerUsersAndCounts]);

  const menus = [
    "Dashboard",
    "Worker Users",
    "Client Users",
    "Jobs",
    "Messages",
    "Billing - Invoicing",
    "Notifications",
    "Account Settings",
  ];

  const menuIcons = {
    Dashboard: (
      <MdDashboard className="icon-style" style={{ color: getRandomColor() }} />
    ),
    Messages: (
      <TiMessages className="icon-style" style={{ color: getRandomColor() }} />
    ),
    "Worker Users": (
      <ImUsers className="icon-style" style={{ color: getRandomColor() }} />
    ),
    "Client Users": (
      <ImUsers className="icon-style" style={{ color: getRandomColor() }} />
    ),
    Jobs: (
      <FaBriefcase className="icon-style" style={{ color: getRandomColor() }} />
    ),
    Notifications: (
      <FaRegBell className="icon-style" style={{ color: getRandomColor() }} />
    ),
    "Billing - Invoicing": (
      <FaDollarSign
        className="icon-style"
        style={{ color: getRandomColor() }}
      />
    ),
    "Account Settings": (
      <FaUserCog className="icon-style" style={{ color: getRandomColor() }} />
    ),
  };

  const profileImageUrl = adminUser?.profilePhoto
    ? `${baseURL}/v1/client-users/profile-images/${adminUser.profilePhoto}`
    : "/images/avatar.jpg";

  const toggleSidebar = () => {
    setShowSidebar((prevState) => {
      return !prevState;
    });
  };

  const handleAccountItemClick = (item) => {
    console.log("Clicked Account item:", item);
    setShowAccountPane((prevState) => ({ ...prevState, show: false }));
  };

  function getRandomColor() {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  const renderContent = () => {
    return <Outlet context={{ adminUser }} />;
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const getMenuLinks = () => {
    return menus.map((menu) => {
      let path;
      if (menu === "Dashboard") {
        path = "/account/admin-dashboard";
      } else if (menu === "Billing - Invoicing") {
        path = "/account/admin-dashboard/billing-invoicing/invoices";
      } else if (menu === "Worker Users") {
        path = "/account/admin-dashboard/users/worker-users";
      } else if (menu === "Client Users") {
        path = "/account/admin-dashboard/users/client-users";
      } else {
        path = `/account/admin-dashboard/${menu
          .toLowerCase()
          .replace(/\s+/g, "-")}`;
      }

      return (
        <li key={menu}>
          <Link to={path} className="menu-item-link">
            {menuIcons[menu]}
            {menu}
            {menu === "Notifications" && totalUnverifiedWorkerCount > 0 && (
              <span className="unread-message-count">
                {totalUnverifiedWorkerCount}
              </span>
            )}
          </Link>
        </li>
      );
    });
  };

  if (!adminUser) {
    return (
      <div className="loading-spinner">
        <ThreeDots
          visible={true}
          height="80"
          width="40"
          color="#4fa94d"
          radius="9"
          ariaLabel="three-dots-loading"
          wrapperStyle={{}}
          wrapperClass=""
        />
      </div>
    );
  }

  return (
    <div className="dashboard-overall-wrapper">
      <>
        <AdminAccountDashboardNavbar
          ref={navbarRef}
          toggleSidebar={toggleSidebar}
          showNotificationPane={showNotificationPane}
          setShowNotificationPane={setShowNotificationPane}
          showAccountPane={showAccountPane}
          setShowAccountPane={setShowAccountPane}
          profileImageUrl={profileImageUrl}
          notificationIconRef={notificationIconRef}
          accountIconRef={accountIconRef}
        />
        {showNotificationPane.show && (
          <div
            ref={notificationPaneRef}
            className="notifications-pane"
            style={{
              position: "fixed",
              top: isMobile ? "auto" : `${showNotificationPane.top}px`,
              left: `${showNotificationPane.left}px`,
              bottom: isMobile ? `${showNotificationPane.bottom}px` : "auto",
              transform: "translateX(-50%)",
              width: `${Math.max(250, showNotificationPane.iconWidth)}px`,
            }}
          >
            <h4>Alerts</h4>
            <ul>
              {[
                "Phone number not verified",
                "Profile photo awaiting approval.",
                "Bio awaiting approval.",
              ].map((item, index) => (
                <li key={index} onClick={() => handleAccountItemClick(item)}>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
        {showAccountPane.show && (
          <div
            ref={accountPaneRef}
            className={`account-pane ${isMobile ? "mobile" : ""}`}
            style={{
              position: "fixed",
              top: isMobile ? "auto" : `${showAccountPane.top}px`,
              bottom: isMobile ? `${showAccountPane.bottom}px` : "auto",
              right: isMobile ? "10px" : "auto",
              left: isMobile ? "auto" : `${showAccountPane.left}px`,
              width: isMobile ? "30%" : "auto",
            }}
          >
            <h4>Account</h4>
            <ul>
              {["Profile", "Settings", "Account & Privacy", "Help"].map(
                (item, index) => (
                  <li key={index} onClick={() => handleAccountItemClick(item)}>
                    {item}
                  </li>
                )
              )}
              <li onClick={handleLogout}>Logout</li>
            </ul>
          </div>
        )}
      </>
      <div
        ref={dashboardMainContainerRef}
        className={`dashboard-main-container ${showSidebar ? "sidebar-open" : ""
          }`}
      >
        <div
          className={`sidebar ${showSidebar ? "show" : ""}`}
          ref={sidebarRef}
        >

          <div className="sidebar-profile">
            <img src={profileImageUrl} alt="Profile Thumbnail" />
            {adminUser && (
              <span>
                {adminUser.firstName} {adminUser.lastName}
              </span>
            )}
            <div className="sidebar-profile-buttons">
              <Link
                className="edit-profile-button"
                to="/account/ps-dashboard/profile/availability"
                onClick={toggleSidebar}
              >
                My Profile
              </Link>
              <button className="logout-button" onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>
          <ul className="sidebar-menu">{getMenuLinks()}</ul>
          {isMobile && showSidebar && (
            <div className="sidebar-close-btn" onClick={toggleSidebar}>
              X
            </div>
          )}
        </div>

        <div
          className={`dashboard-content-container ${showSidebar ? "sidebar-open" : ""
            }`}
          ref={contentRef}
        >
          {renderContent()}
        </div>
        <WarningModal
          show={showWarning}
          remainingTime={remainingTime}
          onStayLoggedIn={resetTimer}
        />
      </div>
    </div>
  );
};

export default Dashboard;
